/**
 * Scanned Results 페이지의 표 소스.
 *
 * BE 는 후보를 **배열 하나로** 준다(페이지네이션 파라미터가 없다). 그래서 조회는 한 번이고
 * 검색은 가진 배열을 다시 거른다 — Datasets 목록과 같은 규칙이다.
 *
 * 수락 · 폐기는 그 행만 목록에서 뺀다. 조직 목록을 통째로 다시 받지 않는다 — 훑기가 도는 중이면
 * 다시 받은 목록에 새 후보가 섞여 들어와 방금 무엇을 처리했는지 흐려진다.
 *
 * Owns:
 * - 조회 시점, 클라이언트 검색, 행 생성, 결정된 행의 제거.
 *
 * Does not own:
 * - 결정 요청 — `api/candidateApi.ts`. 셀 표현 — `candidateColumns.tsx`.
 */
import type { ReactNode } from "react";
import { DataTableSource } from "@carbon6/c6-frontend-design";
import type {
  DataTablePageReq,
  DataTablePageResp,
  DataTableSortState,
} from "@carbon6/c6-frontend-design";
import { listCandidates } from "../api/candidateApi";
import type { Candidate } from "../domain/candidate";
import {
  candidateFilterOptions,
  EMPTY_CANDIDATE_FILTERS,
  matchesCandidateFilters,
  type CandidateFilters,
} from "../feature/candidateFilters";
import { CandidateTableRow } from "./CandidateTableRow";
import { candidateInitialSort } from "./candidateColumns";
import { LIST_PAGE_SIZE, paginateRows } from "./paginateRows";

export type CandidateTableSourceOptions = {
  renderActions?: (candidate: Candidate) => ReactNode;
  resolveErrorMessage: (error: unknown) => string;
};

export class CandidateTableSource extends DataTableSource<CandidateTableRow> {
  private readonly rowOptions: { renderActions?: (c: Candidate) => ReactNode };
  private filters: CandidateFilters = EMPTY_CANDIDATE_FILTERS;
  private loaded: readonly Candidate[] = [];
  private hasLoaded = false;

  constructor({ renderActions, resolveErrorMessage }: CandidateTableSourceOptions) {
    super({ resolveErrorMessage, initialPageSize: LIST_PAGE_SIZE });
    this.rowOptions = { renderActions };
    this.sortState = candidateInitialSort;
  }

  override get sortsOnServer(): boolean {
    return true;
  }

  override setSort(sort: DataTableSortState | null): void {
    const changed =
      this.sortState?.columnKey !== sort?.columnKey ||
      this.sortState?.direction !== sort?.direction;
    super.setSort(sort);
    if (changed && this.getSnapshot().loadState !== "idle") void this.goToPage(1);
  }

  /** 조회 결과는 캐시하고 URL 조건만 다시 적용한다. */
  async applyFilters(next: CandidateFilters): Promise<void> {
    const unchanged = JSON.stringify(next) === JSON.stringify(this.filters);
    this.filters = next;
    if (unchanged && this.hasLoaded) {
      return;
    }
    await this.goToPage(1);
  }

  getFilterOptions() {
    return candidateFilterOptions(this.loaded);
  }

  /** 사용자가 누른 새로고침 — 캐시한 배열을 버리고 서버에 다시 묻는다. */
  async reload(): Promise<void> {
    this.hasLoaded = false;
    await this.refresh();
  }

  /** 수락 · 폐기가 끝난 후보를 목록에서 뺀다. */
  async removeCandidate(candidateUuid: string): Promise<void> {
    this.loaded = this.loaded.filter((row) => row.uuid !== candidateUuid);
    await this.refresh();
  }

  protected override async onPage(
    req: DataTablePageReq,
  ): Promise<DataTablePageResp<CandidateTableRow>> {
    if (!this.hasLoaded) {
      this.loaded = await listCandidates();
      this.hasLoaded = true;
    }

    const visible = this.loaded.filter((candidate) =>
      matchesCandidateFilters(candidate, this.filters),
    );
    const rows = visible.map((candidate) => new CandidateTableRow(candidate, this.rowOptions));

    return paginateRows(rows, req, this.sortState);
  }
}
